import {Injectable} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

/**
 * Service to be used to register frontend validators and have a common place to check if the overall cps form is valid.
 */

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CpsConfiguratorFormValidationService {
  private formControls: Map<UntypedFormControl, Subscription> = new Map<UntypedFormControl, Subscription>();
  private hasValidationErrorSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Registers the FormControl to stay up to date with the validation status for the cps form.
   *
   * @param formControl FormControl to register
   */
  public registerFormControl(formControl: UntypedFormControl): void {
    if (this.formControls.has(formControl)) {
      return;
    }
    this.formControls.set(
      formControl,
      formControl.statusChanges.pipe(untilDestroyed(this)).subscribe((value) => {
        this.updateValidationStatus();
      }),
    );
    this.updateValidationStatus();
  }

  /**
   * Unregister the FormControl to no longer track its status for the cps form.
   *
   * @param formControl FormControl to unregister
   */
  public unregisterFormControl(formControl: UntypedFormControl): void {
    if (!this.formControls.has(formControl)) {
      return;
    }
    const controlsSubscription = this.formControls.get(formControl);
    if (controlsSubscription) {
      controlsSubscription.unsubscribe();
    }
    this.formControls.delete(formControl);
    this.updateValidationStatus();
  }

  /**
   * Observable status for the cps form validation.
   *
   * @return Observable<boolean> that contains the current status of the validation
   */
  public hasValidationError(): Observable<boolean> {
    return this.hasValidationErrorSubject.asObservable().pipe(distinctUntilChanged());
  }

  private updateValidationStatus(): void {
    let foundIssue = false;
    this.formControls.forEach((_, formControl) => {
      if (!formControl.valid) {
        foundIssue = true;
      }
    });
    this.hasValidationErrorSubject.next(foundIssue);
  }
}
